import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CompanyShell } from "@/components/CompanyShell";
import { Briefcase, Plus, Loader2, MapPin, Trash2, Link as LinkIcon, CalendarDays, Users, Eye, EyeOff, Globe2, Power } from "lucide-react";

const VIS_LABEL = { company_only: "Iba firemná databáza", candidate_access: "Kandidátsky prístup", both: "Obe možnosti" };

export default function Jobs() {
  const nav = useNavigate();
  const [jobs, setJobs] = useState(null);

  const load = () => api.get("/company/jobs").then((r) => setJobs(r.data.jobs)).catch(() => setJobs([]));
  useEffect(() => { load(); }, []);

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Vymazať túto ponuku?")) return;
    try { await api.delete(`/company/jobs/${id}`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleActive = async (j, e) => {
    e.stopPropagation();
    try {
      await api.put(`/company/jobs/${j.id}`, { ...j, active: j.active === false });
      toast.success(j.active === false ? "Ponuka aktivovaná" : "Ponuka deaktivovaná");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <CompanyShell>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight">Moje pracovné ponuky</h1>
          <p className="mt-2 text-slate-600">Vytvorte ponuku z URL, textu alebo ručne. V detaile nájdete vhodných kandidátov z databázy.</p>
        </div>
        <button data-testid="new-job-btn" onClick={() => nav("/company/jobs/new")}
          className="inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-6 py-3 font-semibold transition-colors active:scale-95">
          <Plus size={18} /> Nová ponuka
        </button>
      </div>

      {jobs === null ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-700" /></div>
      ) : jobs.length === 0 ? (
        <div className="mt-8 rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50"><Briefcase className="text-blue-700" /></div>
          <p className="mt-4 font-semibold text-slate-800">Žiadne pracovné ponuky</p>
          <p className="text-sm text-slate-500 mt-1">Vytvorte prvú ponuku vložením linku alebo textu inzerátu.</p>
          <button data-testid="empty-new-job-btn" onClick={() => nav("/company/jobs/new")}
            className="mt-5 inline-flex items-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-6 py-3 font-semibold transition-colors active:scale-95">
            <Plus size={17} /> Nová ponuka
          </button>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="jobs-list">
          {jobs.map((j) => (
            <div key={j.id} data-testid={`job-${j.id}`} onClick={() => nav(`/company/jobs/${j.id}`)}
              className={`rounded-3xl bg-white border shadow-sm p-6 hover:shadow-md transition-all cursor-pointer ${j.active === false ? "border-slate-200 opacity-75" : "border-slate-200"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-head font-semibold text-lg truncate">{j.title}</h3>
                  <p className="text-sm text-slate-500 truncate">{j.company}</p>
                </div>
                <button data-testid={`delete-job-${j.id}`} onClick={(e) => remove(j.id, e)} className="p-1.5 text-slate-400 hover:text-red-600 transition-colors"><Trash2 size={16} /></button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-500">
                {j.location && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {j.location}</span>}
                {j.employment_type && <span className="rounded-full bg-slate-100 px-2 py-0.5">{j.employment_type}</span>}
                {j.created_at && <span className="inline-flex items-center gap-1"><CalendarDays size={13} /> {new Date(j.created_at).toLocaleDateString("sk-SK")}</span>}
                {j.source_url && <span className="inline-flex items-center gap-1 text-blue-600"><LinkIcon size={12} /> z URL</span>}
              </div>
              {/* Stav + publikovanie v Kandidátskom prístupe */}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span data-testid={`job-active-badge-${j.id}`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${j.active === false ? "bg-slate-100 text-slate-500" : "bg-emerald-50 border border-emerald-100 text-emerald-700"}`}>
                  <Power size={12} /> {j.active === false ? "Neaktívna" : "Aktívna"}
                </span>
                <span data-testid={`job-visibility-badge-${j.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600">
                  <Globe2 size={12} /> {VIS_LABEL[j.visibility] || "Iba firemná databáza"}
                </span>
                <span data-testid={`job-published-badge-${j.id}`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${j.published_candidate_access ? "bg-blue-50 border border-blue-100 text-blue-700" : "bg-slate-50 border border-slate-200 text-slate-500"}`}>
                  {j.published_candidate_access ? <Eye size={12} /> : <EyeOff size={12} />} Kandidátsky prístup: {j.published_candidate_access ? "áno" : "nie"}
                </span>
                <button data-testid={`toggle-active-${j.id}`} onClick={(e) => toggleActive(j, e)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                  <Power size={12} /> {j.active === false ? "Aktivovať" : "Deaktivovať"}
                </button>
              </div>
              {j.required_skills?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {j.required_skills.slice(0, 5).map((s, i) => (
                    <span key={i} className="rounded-full bg-blue-50 border border-blue-100 px-2.5 py-1 text-xs font-medium text-blue-700">{s}</span>
                  ))}
                </div>
              )}
              <div className="mt-3">
                <span data-testid={`job-match-count-${j.id}`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${j.match_count > 0 ? "bg-emerald-50 border border-emerald-100 text-emerald-700" : "bg-slate-50 border border-slate-200 text-slate-500"}`}>
                  <Users size={13} /> {j.match_count > 0 ? `${j.match_count} vhodných kandidátov` : "Zatiaľ bez vyhľadania"}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </CompanyShell>
  );
}
